import Axios, { AxiosInstance } from "axios";
import { authenticationProtocolsUrl, loginUrl, loginTestUrl } from "../helpers/urlHelper";
import { IAuthenticationInformation } from "../interfaces/configuration/IAuthenticationInformation";
import { addHours, addDays } from "date-fns";
import { IAuthenticationProtocol } from "../interfaces/configuration/IAuthenticationProtocol";

export const loginMfilesHttpClientAsync = async (
  mfilesApiBaseUrl: string,
  authenticationInformation: IAuthenticationInformation,
  rememberMe: boolean,
): Promise<AxiosInstance> => {
  const apiToken = await loginToMfilesAsync(mfilesApiBaseUrl, authenticationInformation);
  setStoredApiToken(apiToken, rememberMe);
  return createMfilesHttpClient(mfilesApiBaseUrl, apiToken);
};

export const loginMfilesHttpClientOAuthAsync = async (
  mfilesApiBaseUrl: string,
  apiToken: string,
  vault: string
): Promise<AxiosInstance> => {
  const headers = { "Authorization": `Bearer ${apiToken}`, "X-Vault": vault, "Content-Type": "application/json; charset=utf-8" };
  const presharedKey = getPresharedKey();
  if(presharedKey) {
    headers['X-PresharedKey'] = presharedKey;
  }
  // Let's test the token.
  await Axios.get(loginTestUrl(), { headers: headers, baseURL: mfilesApiBaseUrl });
  return Axios.create({
    timeout: 60000,
    headers: headers,
    baseURL: mfilesApiBaseUrl,
  });
};

export const createMfilesHttpClient = (mfilesApiBaseUrl: string, apiKey: string): AxiosInstance => {
  const headers = { "X-Authentication": apiKey, "Content-Type": "application/json; charset=utf-8" };
  const presharedKey = getPresharedKey();
  if(presharedKey) {
    headers['X-PresharedKey'] = presharedKey;
  }
  return Axios.create({
    timeout: 60000,
    headers: headers,
    baseURL: mfilesApiBaseUrl,
  });
};

export const loginToMfilesAsync = async (mfilesApiBaseUrl: string, authenticationInformation: IAuthenticationInformation): Promise<string> => {
  const url = loginUrl();
  const headers = { "Content-Type": "application/json; charset=utf-8" };
  const presharedKey = getPresharedKey();
  if(presharedKey) {
    headers['X-PresharedKey'] = presharedKey;
  }
  //MFWA login cookie may prevent authentication
  //Using fetch instead of axios since fetch doesn't send cookies by default
  const response = await fetch(mfilesApiBaseUrl + url, {
    method: 'POST',
    headers: headers,
    credentials: 'omit',
    body: JSON.stringify(authenticationInformation)
  });
  const json = await response.json();
  const apiToken = json?.Value;
  if(apiToken) {
    headers['X-Authentication'] = apiToken;
  }

  // Every authentication attempt results in a token. The token just might not work... because M-files.
  // Let's test the token.
  await Axios.get(loginTestUrl(), { headers: headers, baseURL: mfilesApiBaseUrl }); // Throws 403 if not logged in

  return apiToken;
};

export const getAuthenticationProtocols = async(mfilesApiBaseUrl: string, vault: string): Promise<IAuthenticationProtocol[]> => {
  const url = authenticationProtocolsUrl(vault);
  const headers = { "Content-Type": "application/json; charset=utf-8" };
  const presharedKey = getPresharedKey();
  if(presharedKey) {
    headers['X-PresharedKey'] = presharedKey;
  }
  const response = await Axios.get(url, { baseURL: mfilesApiBaseUrl, headers: headers });
  return response.data as IAuthenticationProtocol[];
};

const TOKEN_KEY = "mfilesRestApiAuthenticationToken";
const TOKEN_VALID_SLIDING = "mfilesRestApiAuthenticationValidUntil";
const TOKEN_VALID_ABSOLUTE = "mfilesRestApiAuthenticationValidAbsolute";
const TOKEN_LIFETIME = 8; //Hours
const TOKEN_LIFETIME_ABSOLUTE = 30; //Days
const PRESHARED_KEY = "mfilesRestApiPresharedKey";

const setSlidingExpirationDate = () => localStorage.setItem(TOKEN_VALID_SLIDING, addHours(new Date(), TOKEN_LIFETIME).toISOString());
const setAbsoluteExpirationDate = () => localStorage.setItem(TOKEN_VALID_ABSOLUTE, addDays(new Date(), TOKEN_LIFETIME_ABSOLUTE).toISOString());

export const clearStoredApiToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_VALID_SLIDING);
  localStorage.removeItem(TOKEN_VALID_ABSOLUTE);
};

export const getStoredApiToken = (): string | null => {
  const absoluteExpirationDate = localStorage.getItem(TOKEN_VALID_ABSOLUTE);
  if(absoluteExpirationDate && new Date(absoluteExpirationDate) > new Date()) {
    return localStorage.getItem(TOKEN_KEY); 
  }
  if(new Date(localStorage.getItem(TOKEN_VALID_SLIDING)) > new Date()) {
    setSlidingExpirationDate();
    return localStorage.getItem(TOKEN_KEY); 
  }
  //Clear token if authentication expired
  clearStoredApiToken();
  return null;
};
const setStoredApiToken = (token: string, rememberMe: boolean) => {
  localStorage.setItem(TOKEN_KEY, token);
  if(rememberMe) {
    setAbsoluteExpirationDate();
  } else {
    setSlidingExpirationDate();
  }
};

export const validateApiToken = async (mfilesApiBaseUrl: string, apiToken: string): Promise<[boolean, string]> => {
  const headers = { "X-Authentication": apiToken, "Content-Type": "application/json; charset=utf-8" };
  const presharedKey = getPresharedKey();
  if(presharedKey) {
    headers['X-PresharedKey'] = presharedKey;
  }
  try {
    await Axios.get(loginTestUrl(), { headers: headers, baseURL: mfilesApiBaseUrl });
  } catch (err) {
    const message = err?.response?.data?.Message ?? 'Unknown error';
    return [false, message];
  }
  return [true,"OK"];
}

export const getPresharedKey = (): string => {
  return localStorage.getItem(PRESHARED_KEY); 
}

export const setPresharedKey = (key: string): void => {
  localStorage.setItem(PRESHARED_KEY, key);
}

export const clearPresharedKey = (): void => {
  localStorage.removeItem(PRESHARED_KEY);
}

